/* Common Imports */

import { styled, Theme, useMediaQuery, useTheme } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { NextRouter } from "next/router";
import StateInterface from "../../../../redux-magic/state-interface";
import { deleteChatThunk, setChatScreenThunk, setOpenChatModalThunk } from "../../../../redux-magic/store";
import { Participants } from "../../../../redux-magic/sub-interfaces/chat-interface";

/* Component Imports */

import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	MenuItem,
	Popover,
	SvgIcon,
	Tooltip,
	Typography,
} from "@mui/material";

/* Icon Imports */

import { ArrowBackOutlined, ExpandMoreOutlined, MoreVertOutlined } from "@mui/icons-material";
import CallIcon from "@mui/icons-material/Call";
import PremiumBadge from "/public/images/badges/verified_premium.svg";

/* Funtion Imports */

import BusinessProfileTypeString from "../../../../lib/BusinessProfileTypeString";
import BeegruButton from "../../../common-components/buttons/BeegruButton";

/* Styled Components */

const RootDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	borderBottom: "#E5E5E5 1px solid",
	padding: "1.5rem 1.25rem 1.25rem 1.25rem",
	[theme.breakpoints.up("md1190")]: {
		// padding: "1.5rem 1.25rem 1rem 1.25rem",
	},
}));

const ColumnContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	height: "100%",
	padding: "0rem",
	gap: "0.25rem",
	overflow: "hidden",
}));

const RowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
}));

const CustomImg = styled("img")(({ theme }) => ({
	borderRadius: "0.5rem",
	boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
	height: "3rem",
	width: "3rem",
	[theme.breakpoints.up("md1190")]: {
		height: "3rem",
		width: "3rem",
	},
}));

const Heading = styled(Typography)(({ theme }) => ({
	fontSize: "0.875rem",
	fontWeight: 500,
	cursor: "pointer",
	color: "#6e3037",
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
	},
}));

const SubHeading = styled(Typography)(({ theme }) => ({
	fontSize: "0.825rem",
	fontWeight: 400,
	color: theme.palette.text.secondary,
	[theme.breakpoints.up("md1190")]: {
		fontSize: "0.875rem",
	},
}));

const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
	fontSize: "0.825rem",
	padding: "0rem 0.75rem 0rem 0.75rem",
	borderRadius: "0.75rem",
	width: "100%",
	[theme.breakpoints.up("md1190")]: {
		fontSize: "0.875rem",
		padding: "1rem 1rem 1rem 1rem",
	},
}));

const ChatHeader = ({
	session,
	dispatch,
	router,
	profile_context,
	chat_id,
	chat_type,
	chat_title,
	chat_description,
	chat_is_broadcast,
	private_chat_receiver,
	desktop_fullscreen_mode,
}: {
	session: StateInterface["redux_session"];
	dispatch: Function;
	router: NextRouter;
	profile_context: any;
	chat_id: string;
	chat_type: string;
	chat_title: string;
	chat_description: string;
	chat_is_broadcast: boolean;
	private_chat_receiver: Participants;
	desktop_fullscreen_mode: boolean;
}) => {
	const theme = useTheme();

	// Lets check if the device is a mobile device or not

	const smDevice = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

	/* Translations */

	const localLang = {
		verified: router.locale === "ar-AE" ? "تم التحقق" : router.locale === "kn-IN" ? "ಪರಿಶೀಲಿಸಲಾಗಿದೆ" : "Verified",
		premium_user:
			router.locale === "ar-AE" ? "مستخدم مميز" : router.locale === "kn-IN" ? "ಪ್ರೀಮಿಯಂ ಬಳಕೆದಾರ" : "Premium user",
		client: router.locale === "ar-AE" ? "عميل" : router.locale === "kn-IN" ? "ಗ್ರಾಹಕ" : "Client",
		chat_info: router.locale === "ar-AE" ? "معلومات الدردشة" : router.locale === "kn-IN" ? "ಚಾಟ್ ಮಾಹಿತಿ" : "Chat info",
		delete_chat: router.locale === "ar-AE" ? "حذف الدردشة" : router.locale === "kn-IN" ? "ಚಾಟ್ ಅಳಿಸಿ" : "Delete chat",
		back_to_chats:
			router.locale === "ar-AE"
				? "العودة إلى الدردشات"
				: router.locale === "kn-IN"
					? "ಚಾಟ್‌ಗಳಿಗೆ ಹಿಂತಿರುಗಿ"
					: "Back to chats",
		options: router.locale === "ar-AE" ? "خيارات" : router.locale === "kn-IN" ? "ಆಯ್ಕೆಗಳು" : "Options",
		close: router.locale === "ar-AE" ? "إغلاق" : router.locale === "kn-IN" ? "ಮುಚ್ಚಿ" : "Close",
		yes_delete: router.locale === "ar-AE" ? "نعم، احذف" : router.locale === "kn-IN" ? "ಹೌದು, ಅಳಿಸಿ" : "Yes, delete",
		no_cancel: router.locale === "ar-AE" ? "لا، إلغاء" : router.locale === "kn-IN" ? "ಇಲ್ಲ, ರದ್ದುಮಾಡಿ" : "No, cancel",
		delete_chat_confirmation:
			router.locale === "ar-AE"
				? "هل أنت متأكد أنك تريد حذف هذه المحادثة؟"
				: router.locale === "kn-IN"
					? "ಈ ಚಾಟ್ ಅನ್ನು ಅಳಿಸಲು ನೀವು ಖಚಿತವಾಗಿ ಬಯಸುವಿರಾ?"
					: "Are you sure you want to delete this chat?",
	};

	/* Options Menu */

	const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);

	const handleOptionsMenuClick = (event: React.MouseEvent<Element>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleOptionsMenuClose = () => {
		setAnchorEl(null);
	};

	const open = Boolean(anchorEl);
	const id = open ? "simple-popover" : undefined;

	/* Delete Dialog */

	const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);

	const handleOpenDeleteDialog = () => {
		setOpenDeleteDialog(true);
		handleOptionsMenuClose(); // Close options menu
	};

	const handleCloseDeleteDialog = () => {
		setOpenDeleteDialog(false);
	};

	const handleDeleteChat = () => {
		dispatch(
			deleteChatThunk({
				chat_id: chat_id,
				is_business_profile: profile_context.is_business_profile,
				user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
				business_profile_id: profile_context.business_profile_id,
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
			}),
		);
		handleCloseDeleteDialog();
	};

	/* Premium Tooltip */

	const [openPremiumTooltip, setOpenPremiumTooltip] = React.useState(false);

	/* Chat Information */

	const is_user_business_profile: boolean =
		private_chat_receiver.is_business_profile === true &&
		typeof private_chat_receiver.business_profile_id === "object" &&
		private_chat_receiver.business_profile_id !== null;

	// Let's cxheck if the business user has show_contact_details set to true

	const is_business_user_allowd_calles: boolean =
		private_chat_receiver.is_business_profile === true &&
		typeof private_chat_receiver.business_profile_id === "object" &&
		private_chat_receiver.business_profile_id !== null &&
		"show_contact_details" in private_chat_receiver.business_profile_id &&
		private_chat_receiver.business_profile_id.show_contact_details === true;

	// Let's check if the use is allowed to call values are is_user_business_profile and smDevice and is_business_user_allowd_calles should return true

	const can_user_call: boolean = is_user_business_profile && smDevice && is_business_user_allowd_calles;

	const chat_name: string =
		private_chat_receiver.is_business_profile === true
			? typeof private_chat_receiver.business_profile_id === "object" &&
				"title" in private_chat_receiver.business_profile_id
				? (private_chat_receiver.business_profile_id.title as string)
				: ""
			: typeof private_chat_receiver.user_id === "object" &&
				  "first_name" in private_chat_receiver.user_id &&
				  "last_name" in private_chat_receiver.user_id
				? (private_chat_receiver.user_id.first_name as string) +
					" " +
					(private_chat_receiver.user_id.last_name as string)
				: "";

	const chat_user_type: string =
		private_chat_receiver.is_business_profile === true
			? typeof private_chat_receiver.business_profile_id === "object" &&
				"business_profile_type" in private_chat_receiver.business_profile_id
				? (private_chat_receiver.business_profile_id.business_profile_type as string)
				: ""
			: localLang.client;

	const chat_user_slug: string =
		private_chat_receiver.is_business_profile === true
			? typeof private_chat_receiver.business_profile_id === "object" &&
				"slug" in private_chat_receiver.business_profile_id
				? (private_chat_receiver.business_profile_id.slug as string)
				: ""
			: typeof private_chat_receiver.user_id === "object" && "_id" in private_chat_receiver.user_id
				? (private_chat_receiver.user_id._id as string)
				: "";

	const chat_user_phone_with_country_code: string =
		private_chat_receiver.is_business_profile === true
			? typeof private_chat_receiver.business_profile_id === "object" &&
				"phone" in private_chat_receiver.business_profile_id &&
				private_chat_receiver.business_profile_id.phone.phone_number
				? ((private_chat_receiver.business_profile_id.phone.country_code +
						private_chat_receiver.business_profile_id.phone.phone_number) as string)
				: ""
			: typeof private_chat_receiver.user_id === "object" &&
				  "phone" in private_chat_receiver.user_id &&
				  private_chat_receiver.user_id.phone
				? ((private_chat_receiver.user_id.country_code + private_chat_receiver.user_id.phone) as string)
				: "";

	const chat_display_picture_url: string =
		private_chat_receiver.is_business_profile === true
			? typeof private_chat_receiver.business_profile_id === "object" &&
				"display_picture_url" in private_chat_receiver.business_profile_id
				? (private_chat_receiver.business_profile_id.display_picture_url.file_id as string)
				: ""
			: typeof private_chat_receiver.user_id === "object" && "profile_picture" in private_chat_receiver.user_id
				? (private_chat_receiver.user_id.profile_picture.file_id as string)
				: "";

	const profilePicture =
		typeof chat_display_picture_url === "string" && chat_display_picture_url.includes("/images/icons/Avatar.svg")
			? "/images/icons/Avatar.svg"
			: chat_is_broadcast
				? chat_display_picture_url
				: `/profile-picture/${chat_display_picture_url}`;

	const chat_premium_subscription_active: boolean =
		private_chat_receiver.is_business_profile === true
			? typeof private_chat_receiver.business_profile_id === "object" &&
				"subscription_information" in private_chat_receiver.business_profile_id &&
				typeof private_chat_receiver.business_profile_id.subscription_information === "object" &&
				private_chat_receiver.business_profile_id.subscription_information !== null &&
				"active" in private_chat_receiver.business_profile_id.subscription_information
				? (private_chat_receiver.business_profile_id.subscription_information as { active?: boolean }).active === true
				: false
			: false;

	// const chat_kyc_status: string =
	// 	private_chat_receiver.is_business_profile === true
	// 		? typeof private_chat_receiver.business_profile_id === "object" &&
	// 			"kyc_verified" in private_chat_receiver.business_profile_id &&
	// 			"kyc_info" in private_chat_receiver.business_profile_id &&
	// 			private_chat_receiver.business_profile_id.kyc_info &&
	// 			typeof private_chat_receiver.business_profile_id.kyc_info === "object" &&
	// 			"kyc_status" in private_chat_receiver.business_profile_id.kyc_info
	// 			? (private_chat_receiver.business_profile_id.kyc_info.kyc_status as string)
	// 			: ""
	// 		: typeof private_chat_receiver.user_id === "object" &&
	// 			  "kyc_verified" in private_chat_receiver.user_id &&
	// 			  "kyc_info" in private_chat_receiver.user_id &&
	// 			  private_chat_receiver.user_id.kyc_info &&
	// 			  typeof private_chat_receiver.user_id.kyc_info === "object" &&
	// 			  "kyc_status" in private_chat_receiver.user_id.kyc_info
	// 			? (private_chat_receiver.user_id.kyc_info.kyc_status as string)
	// 			: "";

	/* Close select dropdowns on scroll */

	React.useEffect(() => {
		const handleScroll = () => {
			setAnchorEl(null);
		};

		window.addEventListener("scroll", handleScroll);
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);

	return (
		<React.Fragment>
			{/* Options Menu */}

			<Popover
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleOptionsMenuClose}
				disableScrollLock={true}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				slotProps={{
					paper: {
						sx: {
							height: "auto",
							borderRadius: "16px",
							padding: "0.5rem",
							gap: "0rem",
							backgroundColor: theme.palette.background.paper,
							backgroundImage: "none",
							scrollbarWidth: "none",
							direction: router.locale === "ar-AE" ? "rtl" : "ltr",
						},
					},
				}}
			>
				{/* <CustomMenuItem>{localLang.chat_info}</CustomMenuItem> */}
				<CustomMenuItem
					onClick={handleOpenDeleteDialog}
					sx={{
						color: theme.palette.error.main,
					}}
				>
					{localLang.delete_chat}
				</CustomMenuItem>
			</Popover>

			{/* Delete Confirmation Modal */}

			<Dialog
				open={openDeleteDialog}
				onClose={handleCloseDeleteDialog}
				aria-labelledby="delete-dialog-title"
				aria-describedby="delete-dialog-description"
				PaperProps={{
					sx: {
						borderRadius: "1rem",
						padding: "0.5rem",
					},
				}}
			>
				<DialogTitle
					id="delete-dialog-title"
					sx={{
						fontSize: "1rem",
						fontWeight: 600,
						color: theme.palette.text.primary,
					}}
				>
					{localLang.delete_chat}
				</DialogTitle>

				<DialogContent>
					<DialogContentText
						id="delete-dialog-description"
						sx={{
							fontSize: "0.875rem",
							color: theme.palette.text.secondary,
						}}
					>
						{localLang.delete_chat_confirmation}
					</DialogContentText>
				</DialogContent>

				<DialogActions sx={{ padding: "1rem" }}>
					<BeegruButton
						onClick={handleDeleteChat}
						variant="text"
						flavor="error"
						sx={{
							textTransform: "none",
							borderRadius: "0.5rem",
						}}
					>
						{localLang.yes_delete}
					</BeegruButton>

					<BeegruButton
						variant="outlined"
						onClick={handleCloseDeleteDialog}
						sx={{
							textTransform: "none",
							color: theme.palette.text.secondary,
						}}
					>
						{localLang.no_cancel}
					</BeegruButton>
				</DialogActions>
			</Dialog>

			{/* Main Content */}

			<RootDiv>
				{/* The condition below hides the back arrow button when the ChatComponent is in fullscreen mode */}

				<RowContainer sx={{ gap: "0.5rem" }}>
					{/* Back Arrow */}

					<Tooltip title={localLang.back_to_chats}>
						<IconButton
							onClick={() => {
								dispatch(setChatScreenThunk("chat_list"));
							}}
						>
							<ArrowBackOutlined
								sx={{
									cursor: "pointer",
									color: theme.palette.text.disabled,
									fontSize: "1.5rem",
								}}
							/>
						</IconButton>
					</Tooltip>

					{/* Avatar Section */}
					<Tooltip title={chat_name}>
						<RowContainer
							onClick={() => {
								router.push(
									`${process.env.PRODUCTION_URL || ""}loading.html?callbackUrl=${encodeURIComponent(
										`${process.env.PRODUCTION_URL || ""}${router.locale}/${chat_user_type}s/${chat_user_slug}`,
									)}&locale=${router.locale || "en-IN"}`,
								);
							}}
							sx={{
								justifyContent: "flex-start",
								alignItems: "flex-start",
								gap: "0.75rem",
							}}
						>
							{/* Profile Picture */}

							<CustomImg
								src={profilePicture as string}
								alt={chat_name}
							/>

							<ColumnContainer
								sx={{
									gap: "0rem",
								}}
							>
								<RowContainer
									sx={{
										justifyContent: "flex-start",
										gap: "0.5rem",
									}}
								>
									{/* Title */}

									<Heading variant="body2">
										{chat_name.length > 14 ? chat_name.substring(0, 14) + "..." : chat_name}
									</Heading>

									{/* Premium Badge */}

									{chat_premium_subscription_active ? (
										<Tooltip
											open={openPremiumTooltip}
											onClick={() => setOpenPremiumTooltip(true)}
											onClose={() => setOpenPremiumTooltip(false)}
											onOpen={() => setOpenPremiumTooltip(true)}
											title={localLang.premium_user}
										>
											<SvgIcon
												component={PremiumBadge}
												inheritViewBox={false}
												viewBox="0 0 24 24"
												width={24}
												height={24}
												sx={{
													width: "1rem",
													height: "1rem",
												}}
											/>
										</Tooltip>
									) : null}
								</RowContainer>

								{/* Profile Type */}

								<SubHeading variant="body2">
									{private_chat_receiver.is_business_profile
										? BusinessProfileTypeString(chat_user_type, router)
										: chat_user_type}
								</SubHeading>
							</ColumnContainer>
						</RowContainer>
					</Tooltip>
				</RowContainer>

				<RowContainer
					sx={{
						justifyContent: "flex-start",
						gap: "0.25rem",
					}}
				>
					{/* Call now */}
					{can_user_call && (
						<Tooltip title={localLang.options}>
							<IconButton
								href={`tel:${chat_user_phone_with_country_code}`}
								target="_blank"
							>
								<CallIcon
									sx={{
										color: theme.palette.success.main,
										fontSize: "1.5rem",
									}}
								/>
							</IconButton>
						</Tooltip>
					)}

					{/* Options Icon */}

					<Tooltip title={localLang.options}>
						<IconButton onClick={handleOptionsMenuClick}>
							<MoreVertOutlined
								sx={{
									color: theme.palette.text.secondary,
									fontSize: "1.5rem",
								}}
							/>
						</IconButton>
					</Tooltip>

					{/* Close Icon */}

					<Tooltip title={localLang.close}>
						<IconButton
							aria-label="close"
							onClick={() => dispatch(setOpenChatModalThunk(false))}
						>
							<ExpandMoreOutlined
								sx={{
									color: theme.palette.text.disabled,
									fontSize: "1.75rem",
								}}
							/>
						</IconButton>
					</Tooltip>
				</RowContainer>
			</RootDiv>
		</React.Fragment>
	);
};

export default ChatHeader;
